import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TransactionApiService from '../../ApiServices/TransactionApiService/TransactionApiService';
import { useAuth } from '../Auth/AuthContext';
import generateTaxInvoice from '../Invoice/GenerateTaxInvoice';
import generateReceipt from '../Invoice/GenerateReciept';
import { DollarSign, MinusCircle, FileText, CreditCard, Paperclip, CheckCircle, Calendar, Clock, Receipt, File, Banknote, User, PiIcon, Globe, ActivityIcon } from "lucide-react";
import InvoiceApiService from '../../ApiServices/InvoiceApiService/InvoiceApiService';

const TransactionTable = () => {
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isPayInvoice, setPayInvoice] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);


    const Auth = useAuth();
    const user = Auth.authState.user;
    const isAdmin = Auth.authState.roles == "Admin";

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const response = await TransactionApiService.GetAllTransactionByUserId(isAdmin ? null : user.id);
            setTransactions(response.data);
            setFilteredTransactions(response.data);
        } catch (error) {
            toast.error('Failed to fetch transactions.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (user && user.id) {
            fetchTransactions();
        }
    }, [user]);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        const filtered = transactions.filter(transaction =>
            transaction.revenue.toString().includes(value) ||
            transaction.deductions.toString().includes(value) ||
            transaction.paymentType.toLowerCase().includes(value.toLowerCase()) ||
            (transaction.isPaid ? 'Paid' : 'Unpaid').toLowerCase().includes(value.toLowerCase())
        );

        setFilteredTransactions(filtered);
    };

    const downloadPDF = async (url) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'file.pdf';
            link.click();
        } catch (error) {
            toast.error('Failed to download PDF.');
        }
    };

    const fetchAndDownloadPDF = async (transactionId) => {
        try {
            const response = await TransactionApiService.GetById(transactionId);
            if (response.data?.deductionImage) {
                downloadDeductionReason(response.data.deductionImage);
            } else {
                toast.error("No PDF data available.");
            }
        } catch (error) {
            toast.error("Failed to fetch PDF.");
        }
    };

    const downloadDeductionReason = (base64) => {
        try {
            const byteCharacters = atob(base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/pdf" });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "DeductionReason.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            toast.error("Failed to download PDF.");
        }
    };

    const handlePayInvoice = async (index) => {
        setPayInvoice(true);
        setCurrentItem(transactions[index]);
    }
    const handleDeleteInvoice = async (index) => {
        const trans = transactions[index];

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This invoice will be permanently deleted!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
        });

        if (result.isConfirmed) {
            try {
                await InvoiceApiService.Delete(trans.id);
                toast.success("Invoice deleted successfully!");
                // Optional: refresh the invoice list here
            } catch (error) {
                toast.error("Failed to delete invoice.");
            }
        }
    };


    const handlePayInvoiceSubmit = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData(e.target);
            formData.append("InvoiceId", currentItem.id); // Ensure field name matches C# model

            await TransactionApiService.PayInvoice(formData);

            setPayInvoice(false);
            fetchTransactions();
            // toast.success('Edit Successfull');
            // fetchData(); // Refresh data after editing
        } catch (error) {
            toast.error('Error updating item:', error.message);
            console.error('Error updating item:', error);
        }
    }

    const DownloadInvoice = async (id) => {
        let invoiceInfo = await TransactionApiService.GetInvoiceInfo(id)
        if (invoiceInfo?.isSuccess && invoiceInfo?.data != null) {
            generateTaxInvoice(invoiceInfo?.data)
        }
    }
    const DownloadReceipt = async (id) => {
        let invoiceInfo = await TransactionApiService.GetInvoiceInfo(id)
        if (invoiceInfo?.isSuccess && invoiceInfo?.data != null) {
            generateReceipt(invoiceInfo?.data);
        }
    }

    const headers = [
        ...(isAdmin
            ? [
                { label: "Publisher", icon: <User size={16} /> },
                { label: "Company", icon: <PiIcon size={16} /> },
                { label: "Web/App", icon: <Globe size={16} /> },
            ]
            : []),
        { label: "Invoice Month", icon: <Calendar size={16} /> },
        { label: "Revenue", icon: <DollarSign size={16} /> },
        { label: "Deductions", icon: <MinusCircle size={16} /> },
        { label: "Deduction-Reason", icon: <FileText size={16} /> },
        { label: "Payment-Type", icon: <CreditCard size={16} /> },
        { label: "Deduction-Attachment", icon: <Paperclip size={16} /> },
        { label: "Paid", icon: <CheckCircle size={16} /> },
        { label: "Payment-Date", icon: <Calendar size={16} /> },
        // { label: "CreatedOn", icon: <Clock size={16} /> },
        { label: "Invoice", icon: <Receipt size={16} /> },
        { label: "Receipt", icon: <File size={16} /> },
    ];



    return (
        <div className="p-6 bg-gray-100 min-h-screen overflow-hidden">
            <ToastContainer />
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Transactions</h2>
                <input
                    type="text"
                    className="border border-gray-300 px-4 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleSearch}
                />
            </div>
            <div className="bg-white shadow-lg rounded-lg p-4">
                {loading ? (
                    <div className="flex justify-center items-center py-10">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-indigo-500"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead className="bg-gradient-to-r from-purple-600 to-pink-500 text-white">
                                <tr>
                                    {headers.map(({ label, icon }) => (
                                        <th key={label} className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider border-b border-gray-300">
                                            <div className="flex items-center gap-2">
                                                {icon} {label}
                                            </div>
                                        </th>
                                    ))}
                                    {isAdmin && (
                                        <>
                                            <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider border-b border-gray-300">
                                                <div className="flex items-center gap-2">
                                                    <Banknote size={16} /> Pay Invoice
                                                </div>
                                            </th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider border-b border-gray-300">
                                                <div className="flex items-center gap-2">
                                                    <ActivityIcon size={16} /> Delete Invoice
                                                </div>
                                            </th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTransactions?.map((transaction, index) => (
                                    <tr key={index} className="border-b hover:bg-gray-100">
                                        {isAdmin && (
                                            <>
                                                <td className="px-4 py-3 text-sm text-gray-600">
                                                    {(transaction.user ? transaction.user?.firstName : '-' || '-') + ' ' + (transaction.user ? transaction?.user?.lastName : '-' || '-')}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600">
                                                    {transaction.user ? transaction.user?.company : '-' || '-'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600">
                                                    {transaction.website ? transaction.website?.url : transaction.app ? transaction.app.name : '-' || '-'}
                                                </td>
                                            </>
                                        )}
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {(transaction?.previousMonth || '-') + ' ' + (transaction?.year || '-')}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {transaction?.revenue != null ? transaction.revenue : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {transaction?.deductions != null ? transaction.deductions : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {transaction?.deductionReason || '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {transaction?.paymentType || '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            <button
                                                onClick={() => fetchAndDownloadPDF(transaction.id)}
                                                className="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600"
                                            >
                                                Download
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {transaction?.isPaid ? 'Paid' : 'Unpaid'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {transaction?.paymentDate ? new Date(transaction.paymentDate).toLocaleDateString() : '-'}
                                        </td>
                                        {/* <td className="px-4 py-3 text-sm text-gray-600">{new Date(transaction.createdOn).toLocaleDateString()}</td> */}
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            <button
                                                onClick={() => DownloadInvoice(transaction.id)}
                                                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                                            >
                                                Download
                                            </button>
                                        </td>
                                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {transaction?.isPaid ? (
                                                <button
                                                    onClick={() => DownloadReceipt(transaction.id)}
                                                    className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                                                >
                                                    Download
                                                </button>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        {isAdmin && (
                                            <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {!transaction?.isPaid ? (
                                                    <button
                                                        onClick={() => handlePayInvoice(index)}
                                                        className="bg-primaryColor text-white px-4 py-2 rounded-md"
                                                    >
                                                        Pay Invoice
                                                    </button>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                        )}
                                        {isAdmin && (
                                            <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <button
                                                    onClick={() => handleDeleteInvoice(index)}
                                                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition"
                                                >
                                                    Delete Invoice
                                                </button>
                                            </td>


                                        )}
                                    </tr>
                                ))}

                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {isPayInvoice && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white p-5 rounded shadow-lg w-1/2">
                        <h2 className="text-2xl font-bold mb-4">Pay Invoice</h2>
                        <form onSubmit={handlePayInvoiceSubmit}>
                            <label className="block mb-2">
                                Total Amount:
                                <input
                                    type="text"
                                    readOnly
                                    value={currentItem.revenue}
                                    className="w-full p-2 border border-gray-300 rounded mt-1"
                                />
                            </label>

                            <label className="block mb-2">
                                Deduction Amount:
                                <input
                                    type="number"
                                    name="DeductionAmount"
                                    className="w-full p-2 border border-gray-300 rounded mt-1"
                                />
                            </label>

                            <label className="block mb-2">
                                Deduction Reasoning:
                                <input
                                    type="text"
                                    name="DeductionReason"
                                    className="w-full p-2 border border-gray-300 rounded mt-1"
                                />
                            </label>

                            <label className="block mb-2">
                                Deduction Image:
                                <input
                                    type="file"
                                    name="DeductionImage"
                                    className="w-full p-2 border border-gray-300 rounded mt-1"
                                />
                            </label>

                            <label className="block mb-2">
                                Payment Type:
                                <select
                                    name="PaymentType"
                                    className="w-full p-2 border border-gray-300 rounded mt-1"
                                >
                                    <option value="">Select Payment Method</option>
                                    <option value="Paypal">Paypal</option>
                                    <option value="Wire">Wire Transfer</option>
                                    <option value="Wise">Wise</option>
                                    <option value="Stripe">Stripe</option>
                                    <option value="CreditCard">Credit Card</option>
                                    <option value="BankTransfer">Bank Transfer</option>
                                </select>
                            </label>

                            <div className="flex justify-end mt-4">
                                <button
                                    type="submit"
                                    className="bg-blue-500 hover:bg-blue-600 focus:ring-blue-400 text-white px-4 py-2 rounded mr-2"
                                >
                                    Pay Now
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPayInvoice(false)}
                                    className="bg-gray-500 hover:bg-gray-600 focus:ring-gray-400 text-white px-4 py-2 rounded"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TransactionTable;